import { getState } from './state.js';
import { normalizeSearchTermsDraft } from './listing-draft-payload.js';
import { normalizeTitlePayload } from './interaction/adapters/listing-draft.adapter.js';

// ── Listing Utilities: Multi-language & Deduplication ──────────────

// Marketplace language mapping
export const MARKETPLACE_LANGUAGES = {
  US: { code: 'en', name: 'English', flag: '🇺🇸' },
  CA: { code: 'en', name: 'English', flag: '🇨🇦' },
  UK: { code: 'en', name: 'English', flag: '🇬🇧' },
  AU: { code: 'en', name: 'English', flag: '🇦🇺' },
  DE: { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  FR: { code: 'fr', name: 'Français', flag: '🇫🇷' },
  IT: { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  ES: { code: 'es', name: 'Español', flag: '🇪🇸' },
  JP: { code: 'ja', name: '日本語', flag: '🇯🇵' },
};

/**
 * Get language info for a marketplace
 */
export function getMarketplaceLanguage(marketplace) {
  const code = String(marketplace || '').trim().toUpperCase();
  return MARKETPLACE_LANGUAGES[code] || MARKETPLACE_LANGUAGES.US;
}

export function resolveCurrentMarketplace() {
  const generationMeta = getState('listingGenerationMeta') || null;
  const productInfo = getState('productInfo') || null;
  const businessContext = getState('businessContext') || null;
  const snapshotState = getState('snapshotState') || null;
  const candidates = [
    generationMeta?.marketplace,
    generationMeta?.mergeMeta?.marketplace,
    generationMeta?.mergeMeta?.businessMeta?.marketplace,
    productInfo?.marketplace,
    productInfo?.targetMarket,
    businessContext?.marketplace,
    businessContext?.targetMarket,
    snapshotState?.marketplace,
    getState('marketplace'),
  ];
  return candidates
    .map((value) => String(value || '').trim().toUpperCase())
    .find((value) => /^[A-Z]{2}$/.test(value)) || 'US';
}

/**
 * Extract words from text for deduplication
 * Normalizes text, removes punctuation, splits into words
 */
export function extractWords(text) {
  if (!text) return new Set();

  // Normalize: lowercase, remove special chars except spaces and hyphens
  const normalized = text
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Split into words, filter out short words (< 3 chars) and common stop words
  const words = normalized.split(' ').filter(w => w.length >= 3);

  return new Set(words);
}

/**
 * Deduplicate Search Terms by removing words already used in Title/Bullets/Description
 * @param {string} searchTerms - Original search terms string
 * @param {string} title - Title text
 * @param {string[]} bullets - Array of bullet points
 * @param {string} description - Description text
 * @returns {string} Deduplicated search terms
 */
export function deduplicateSearchTerms(searchTerms, title = '', bullets = [], description = '') {
  if (!searchTerms) return '';

  // Extract all used words from title, bullets, description
  const usedWords = new Set();

  // Add words from title
  extractWords(title).forEach(w => usedWords.add(w));

  // Add words from bullets
  bullets.forEach(bullet => {
    extractWords(bullet).forEach(w => usedWords.add(w));
  });

  // Add words from description
  extractWords(description).forEach(w => usedWords.add(w));

  // Split search terms into individual keywords
  const stKeywords = searchTerms
    .split(/[,\s]+/)
    .map(kw => kw.trim())
    .filter(kw => kw.length > 0);

  // Filter out keywords that contain any used word
  const deduplicated = stKeywords.filter(keyword => {
    const kwWords = extractWords(keyword);
    // Keep keyword only if none of its words are in usedWords
    for (const word of kwWords) {
      if (usedWords.has(word)) {
        return false;
      }
    }
    return true;
  });

  return deduplicated.join(' ');
}

/**
 * @deprecated Import from './byte-counter.js' directly.
 * Kept here as re-export so existing call sites (and any future imports) don't break.
 */
export { getByteSize } from './byte-counter.js';

/**
 * Truncate search terms to fit within byte limit
 */
export function truncateToByteLimit(text, maxBytes = 250) {
  if (!text) return '';

  const encoder = new TextEncoder();
  let bytes = encoder.encode(text);

  if (bytes.length <= maxBytes) return text;

  // Binary search for the right length
  let left = 0;
  let right = text.length;
  let result = '';

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const substr = text.substring(0, mid);
    const subBytes = encoder.encode(substr).length;

    if (subBytes <= maxBytes) {
      result = substr;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  // Trim to last complete word
  return result.replace(/\s+\S*$/, '').trim();
}

/**
 * Get deduplication stats for display
 */
export function getDeduplicationStats(original, deduplicated) {
  const originalCount = original.split(/[,\s]+/).filter(w => w.length > 0).length;
  const deduplicatedCount = deduplicated.split(/[,\s]+/).filter(w => w.length > 0).length;
  const removed = originalCount - deduplicatedCount;

  return {
    original: originalCount,
    deduplicated: deduplicatedCount,
    removed,
    percentage: originalCount > 0 ? Math.round((removed / originalCount) * 100) : 0,
  };
}


/** 批量任务中可用于生成的已完成竞品快照 */
export function getCompletedBatchSnapshots(snapshots) {
  return (snapshots || []).filter((s) => s && s.status === 'completed' && s.asin);
}

/** 多竞品时传给 API 的 ASIN 列表；单竞品返回 undefined（后端用全部） */
export function resolveSnapshotAsinsForApi(batchSnapshots, competitorAsinsIncluded) {
  const list = getCompletedBatchSnapshots(batchSnapshots);
  if (list.length <= 1) return undefined;
  const fallback = new Set(list.map((s) => s.asin));
  const set = competitorAsinsIncluded instanceof Set && competitorAsinsIncluded.size
    ? competitorAsinsIncluded
    : fallback;
  const asins = [...set].filter((asin) => list.some((s) => s.asin === asin));
  return asins.length ? asins : undefined;
}

/**
 * Collect listing draft from store for compliance/scoring/revision APIs.
 * @param {{ full?: boolean, useRevisionSearchTermsDraft?: boolean }} opts
 */
export function collectCurrentListing({ full = false, useRevisionSearchTermsDraft = false } = {}) {
  const sourceTitle = getState('listingDraftTitle') || {};
  const title = normalizeTitlePayload(sourceTitle) || {};
  const hasCanonicalProductTitle = Object.prototype.hasOwnProperty.call(sourceTitle, 'productTitle');
  const hasCanonicalItemHighlights = Object.prototype.hasOwnProperty.call(sourceTitle, 'itemHighlights');
  const productTitle = hasCanonicalProductTitle && !String(sourceTitle.productTitle ?? '').trim()
    ? ''
    : String(title.productTitle || title.titleMain || title.titleEn || '').trim();
  const itemHighlights = hasCanonicalItemHighlights && !String(sourceTitle.itemHighlights ?? '').trim()
    ? ''
    : String(title.itemHighlights || title.titleSub || '').trim();
  const bullets = getState('listingDraftBullets') || {};
  const description = getState('listingDraftDescription') || {};
  const qa = getState('listingDraftQa') || [];
  const canonicalSearchTerms = getState('listingDraftSearchTerms');
  const pendingSearchTerms = getState('revisionSearchTermsDraft');
  const hasPendingSearchTerms = pendingSearchTerms !== null && pendingSearchTerms !== undefined;
  // Step04 edits live in an explicit pending state. The DOM is only a view of
  // that state: submission must still see an intentionally empty pending draft
  // when the editor is unmounted or the user removed every bubble.
  const searchTermsDraft = normalizeSearchTermsDraft(
    useRevisionSearchTermsDraft && hasPendingSearchTerms
      ? pendingSearchTerms
      : (canonicalSearchTerms || {})
  );
  const searchTermsEn = searchTermsDraft.searchTermsEn || '';
  const snapshot = getState('snapshotState') || {};
  const batchSnapshots = getState('batchSnapshots') || [];

  const base = {
    marketplace: resolveCurrentMarketplace(),
    productTitle,
    itemHighlights,
    titleEn: productTitle,
    titleMain: productTitle,
    titleSub: itemHighlights,
    bulletsEn: bullets.bulletsEn || [],
    descriptionEn: description.descriptionEn || '',
    searchTermsEn,
    searchTerms: searchTermsDraft,
    qa,
    brandEn: snapshot.brandEn || snapshot.brand || '',
    asin: snapshot.asin || '',
    competitors: batchSnapshots
      .map((s) => ({
        asin: s.asin || '',
        brandEn: s.brandEn || s.brand || '',
      }))
      .filter((c) => c.asin !== (snapshot.asin || '')),
  };
  if (!full) return base;
  return {
    ...base,
    titleZh: title.titleZh || '',
    titleOptions: title.titleOptions || [],
    recommendation: title.recommendation || '',
    bulletsZh: bullets.bulletsZh || [],
    descriptionZh: description.descriptionZh || '',
    aplusModules: description.aplusModules || [],
  };
}
